<template>
  <div class="docs-page">
    <header class="docs-header">
      <router-link
        to="/"
        class="docs-header__brand"
      >
        <b-img
          class="docs-header__logo"
          :src="companyLogoImage"
          alt="App logo"
        />
      </router-link>
      <div class="docs-header__text">
        <h1 class="docs-header__title">Documentation</h1>
        <p class="docs-header__lead">
          Learn how to use AGVA day to day — click a topic on the left to get started.
        </p>
      </div>
    </header>

    <div class="docs-layout">
      <aside class="docs-tree">
        <div
          v-if="isTreeLoading"
          class="docs-tree__loading text-muted"
        >
          Loading topics…
        </div>
        <ul
          v-else
          class="docs-tree__list"
        >
          <li
            v-for="node in roots"
            :key="node.id"
            class="docs-tree__item"
          >
            <DocsTreeNode
              :node="node"
              :depth="0"
              :active-content="activeContent"
              :expanded-ids="expandedIds"
              :children-map="childrenMap"
              :loading-ids="loadingIds"
              @toggle="onToggle"
              @select="onSelect"
            />
          </li>
        </ul>
      </aside>

      <main class="docs-content">
        <nav
          v-if="breadcrumbs.length"
          class="docs-breadcrumb"
          aria-label="Breadcrumb"
        >
          <router-link to="/documentation">
            Documentation
          </router-link>
          <template v-for="(crumb, index) in breadcrumbs" :key="crumb.content">
            <span class="docs-breadcrumb__sep">/</span>
            <router-link
              v-if="index < breadcrumbs.length - 1"
              :to="crumbPath(index)"
            >
              {{ crumb.name }}
            </router-link>
            <span
              v-else
              class="docs-breadcrumb__current"
            >{{ crumb.name }}</span>
          </template>
        </nav>

        <div
          v-if="isContentLoading"
          class="docs-content__loading text-muted"
        >
          Loading page…
        </div>
        <div
          v-else-if="contentError"
          class="docs-content__error text-danger"
        >
          {{ contentError }}
        </div>
        <div
          v-else-if="htmlBody"
          class="docs-article"
          @click="onArticleClick"
          v-html="htmlBody"
        />
        <div
          v-else
          class="docs-content__welcome"
        >
          <h2>Welcome</h2>
          <p>
            Choose a section from the left menu. Topics with sub-pages expand when you click them.
          </p>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { BImg } from 'bootstrap-vue'
import axios from '@/libs/axios'
import { $themeConfig } from '@themeConfig'
import DocsTreeNode from '@/views/Components/DocsTreeNode.vue'

export default {
  name: 'Documentation',
  components: {
    BImg,
    DocsTreeNode,
  },
  data() {
    const { companyLogoImage } = $themeConfig.app
    return {
      companyLogoImage,
      roots: [],
      childrenMap: {},
      expandedIds: {},
      loadingIds: {},
      isTreeLoading: true,
      isContentLoading: false,
      htmlBody: '',
      contentError: '',
      breadcrumbs: [],
      activeContent: '',
      resolvedPath: '',
    }
  },
  computed: {
    docPath() {
      const param = this.$route.params.docPath
      if (Array.isArray(param)) {
        return param.filter(Boolean).join('/')
      }
      return param ? String(param) : ''
    },
  },
  watch: {
    docPath: {
      immediate: true,
      handler(path) {
        this.loadPage(path)
      },
    },
  },
  async mounted() {
    await this.loadRoots()
  },
  methods: {
    async loadRoots() {
      this.isTreeLoading = true
      try {
        const { data } = await axios.get('api/docs')
        this.roots = data?.data || []
      } catch (e) {
        this.roots = []
      } finally {
        this.isTreeLoading = false
      }
    },
    async ensureChildren(node, { force = false } = {}) {
      if (!force && !node?.has_children) {
        return
      }
      if (this.childrenMap[node.id]) {
        return
      }
      this.loadingIds = { ...this.loadingIds, [node.id]: true }
      try {
        const { data } = await axios.get('api/docs', {
          params: { parent_id: node.id },
        })
        this.childrenMap = {
          ...this.childrenMap,
          [node.id]: data?.data || [],
        }
      } catch (e) {
        this.childrenMap = {
          ...this.childrenMap,
          [node.id]: [],
        }
      } finally {
        const next = { ...this.loadingIds }
        delete next[node.id]
        this.loadingIds = next
      }
    },
    async onToggle(node) {
      const isOpen = Boolean(this.expandedIds[node.id])
      if (isOpen) {
        this.expandedIds = { ...this.expandedIds, [node.id]: false }
        return
      }
      await this.ensureChildren(node, { force: true })
      this.expandedIds = { ...this.expandedIds, [node.id]: true }
    },
    onSelect(node, pathSegments) {
      const path = pathSegments.join('/')
      if (path === this.docPath) {
        return
      }
      this.$router.push({ path: `/documentation/${path}` })
    },
    crumbPath(index) {
      const parts = this.breadcrumbs.slice(0, index + 1).map((c) => c.content)
      return `/documentation/${parts.join('/')}`
    },
    async loadPage(path) {
      this.contentError = ''
      this.htmlBody = ''
      this.breadcrumbs = []
      this.activeContent = ''
      this.resolvedPath = path || ''

      if (!path) {
        this.isContentLoading = false
        return
      }

      this.isContentLoading = true
      try {
        const { data } = await axios.get('api/docs/resolve', {
          params: { path },
        })
        const payload = data?.data
        if (!payload?.node) {
          this.contentError = 'This documentation page was not found.'
          return
        }

        this.activeContent = payload.node.content
        this.breadcrumbs = [...(payload.ancestors || []), payload.node]
        await this.expandAncestors(payload.ancestors || [])

        const htmlUrl = payload.html_url
        const response = await fetch(htmlUrl, { credentials: 'same-origin' })
        if (!response.ok) {
          this.contentError = 'This documentation page is not available yet.'
          return
        }
        this.htmlBody = await response.text()
      } catch (e) {
        this.contentError = 'This documentation page was not found.'
      } finally {
        this.isContentLoading = false
      }
    },
    async expandAncestors(ancestors) {
      for (let i = 0; i < ancestors.length; i += 1) {
        const ancestor = ancestors[i]
        // eslint-disable-next-line no-await-in-loop
        await this.ensureChildren(ancestor, { force: true })
        this.expandedIds = { ...this.expandedIds, [ancestor.id]: true }
      }
    },
    onArticleClick(event) {
      const anchor = event.target.closest('a')
      if (!anchor) {
        return
      }
      const href = anchor.getAttribute('href') || ''
      if (!href.startsWith('/documentation')) {
        return
      }
      event.preventDefault()
      if (href !== this.$route.fullPath) {
        this.$router.push({ path: href })
      }
    },
  },
}
</script>

<style scoped>
.docs-page {
  --docs-ink: #1c1833;
  --docs-muted: #5c5678;
  --docs-accent: #4f439e;
  --docs-line: rgba(79, 67, 158, 0.18);
  --docs-surface: #f4f2fb;

  min-height: 100vh;
  color: var(--docs-ink);
  background:
    radial-gradient(900px 420px at 8% -8%, rgba(79, 67, 158, 0.14), transparent 55%),
    linear-gradient(180deg, #faf9ff 0%, var(--docs-surface) 45%, #eeeaf8 100%);
}

.docs-header {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--docs-line);
  background: rgba(255, 255, 255, 0.72);
}

.docs-header__brand {
  display: inline-flex;
  flex-shrink: 0;
}

.docs-header__logo {
  height: 42px;
  width: auto;
}

.docs-header__title {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 700;
}

.docs-header__lead {
  margin: 0.2rem 0 0;
  color: var(--docs-muted);
  font-size: 0.95rem;
}

.docs-layout {
  display: grid;
  grid-template-columns: minmax(240px, 300px) 1fr;
  gap: 0;
  min-height: calc(100vh - 96px);
}

.docs-tree {
  border-right: 1px solid var(--docs-line);
  background: rgba(255, 255, 255, 0.55);
  padding: 1rem 0.75rem 2rem;
  overflow: auto;
}

.docs-tree__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.docs-tree__loading {
  padding: 0.75rem;
  font-size: 0.9rem;
}

.docs-content {
  padding: 1.5rem 1.75rem 3rem;
  overflow: auto;
}

.docs-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
}

.docs-breadcrumb a {
  color: var(--docs-accent);
  font-weight: 600;
}

.docs-breadcrumb__sep {
  color: var(--docs-muted);
}

.docs-breadcrumb__current {
  color: var(--docs-ink);
  font-weight: 600;
}

.docs-content__welcome h2 {
  margin-top: 0;
}

.docs-content__welcome p,
.docs-content__loading,
.docs-content__error {
  color: var(--docs-muted);
}

.docs-article {
  max-width: 48rem;
  line-height: 1.6;
}

.docs-article :deep(h1) {
  font-size: 1.75rem;
  margin: 0 0 0.75rem;
}

.docs-article :deep(h2) {
  font-size: 1.25rem;
  margin: 1.5rem 0 0.6rem;
}

.docs-article :deep(p),
.docs-article :deep(li) {
  color: #2d2945;
}

.docs-article :deep(ol),
.docs-article :deep(ul) {
  padding-left: 1.25rem;
}

.docs-article :deep(a) {
  color: var(--docs-accent);
  font-weight: 600;
}

.docs-article :deep(strong) {
  font-weight: 700;
}

@media (max-width: 991.98px) {
  .docs-layout {
    grid-template-columns: 1fr;
  }

  .docs-tree {
    border-right: 0;
    border-bottom: 1px solid var(--docs-line);
    max-height: 280px;
  }

  .docs-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
